import { Component, Inject } from '@angular/core';
import { MatDialog, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { InfoDialogComponent } from '../info-dialog/info-dialog.component';
import { CssService } from 'src/app/services/css-service';
import { CurrencyFormat } from '../../waitingnotes.component';

interface ItemToShare {
  id_waiting_note: number;
  noteId: number;
  id: number;
  name: string;
  total_ttc: number;
  uuid: string;
  price: number;
  quantity: number;
  remainingQuantity: number;
  unit_ttc: number;
  alreadyPaid: boolean;
  shared_index: number;
  shared_total: number;
  free: number;
}

interface ItemWithChildren extends ItemToShare {
  children?: ItemWithChildren[];
}

interface SharedItemDetail {
  noteId: number;
  id_product: number;
  quantity_paid: number;
  amount_paid: number;
  uuid: string;
  shared_index: number;
  shared_total: number;
  name: string;
  price: number;
}

@Component({
  selector: 'app-share-items-dialog',
  templateUrl: './share-items-dialog.component.html',
})
export class ShareItemsDialogComponent {
  selectedItems: ItemToShare[] = [];
  sharedParts = 2;
  itemList: ItemWithChildren[];
  skipAlreadyDividedCheck = false;

  constructor(
    public dialogRef: MatDialogRef<ShareItemsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { items: ItemWithChildren[], currencyFormat: CurrencyFormat },
    private dialog: MatDialog,
    public cssService: CssService,
  ) {
    this.itemList = data.items;
  }

  isClosing = false;
  selectedOption: {action: string, item: ItemToShare} | SharedItemDetail[] | null = null;

  toggleItem(item: ItemToShare) {
    if (item.shared_index && !this.skipAlreadyDividedCheck) {
      this.dialog.open(InfoDialogComponent, {
        data: {
          title: 'Information',
          message: 'Cet article est déjà divisé et ne peut plus être sélectionné ici.',
          isError: false
        }
      });
      this.skipAlreadyDividedCheck = false;
    } else {
      const index = this.selectedItems.indexOf(item);
      if (index === -1) {
        this.selectedItems.push(item);
      } else {
        this.selectedItems.splice(index, 1);
      }
    }
  }

  getFreeLabel(item: ItemToShare): string {
    return item.quantity > 1 ? 'Offerts' : 'Offert';
  }

  getPaidLabel(item: ItemToShare): string {
    const paidQuantity = item.quantity - item.remainingQuantity;
    if (item.quantity === 1) return 'payé';
    if (item.remainingQuantity === 0 || paidQuantity > 1) return `${paidQuantity} payés`;
    return `${paidQuantity} payé`;
  }


  isSelected(item: ItemToShare): boolean {
    return this.selectedItems.includes(item);
  }

  isAnyPartPaid(item: ItemToShare): boolean {
    return this.itemList.some(part =>
      part.uuid.startsWith(item.uuid.split('-')[0]) && part.alreadyPaid
    );
  }

  computeTotalTtc(item: ItemWithChildren): number {
    let total = item.total_ttc;

    if (item.children && item.children.length > 0) {
      for (const child of item.children) {
        total += this.computeTotalTtc(child);
      }
    }

    return total;
  }

  increaseParts() {
    this.sharedParts++;
  }

  decreaseParts() {
    if (this.sharedParts > 2) {
      this.sharedParts--;
    }
  }

  confirm() {
    const minPrice = 1;
    const result: SharedItemDetail[] = [];

    for (const item of this.selectedItems) {
      if (this.sharedParts === item.quantity) {
        this.dialog.open(InfoDialogComponent, {
          data: {
            title: 'Erreur',
            message: `Impossible de diviser "${item.name}" en ${this.sharedParts} parts : Veuillez passer par la sélection par article.`,
            isError: true
          }
        });
        return;
      }

      const totalItemTtc = this.computeTotalTtc(item as ItemWithChildren);
      const partAmount = totalItemTtc / this.sharedParts;

      if (partAmount < minPrice) {
        this.dialog.open(InfoDialogComponent, {
          data: {
            title: 'Erreur',
            message: `Impossible de diviser "${item.name}" en ${this.sharedParts} parts : le montant par part tomberait en-dessous de 1 €.`,
            isError: true
          }
        });
        return;
      }
    }

    for (const item of this.selectedItems) {
      const totalItemTtc = this.computeTotalTtc(item as ItemWithChildren);
      let accumulated = 0;

      for (let i = 1; i <= this.sharedParts; i++) {
        let amount = +(totalItemTtc / this.sharedParts).toFixed(2);

        if (i === this.sharedParts) {
          amount = +(totalItemTtc - accumulated).toFixed(2);
        } else {
          accumulated += amount;
        }

        result.push({
          noteId: item.id_waiting_note,
          id_product: item.id,
          quantity_paid: 1,
          amount_paid: amount,
          uuid: item.uuid,
          shared_index: i,
          shared_total: this.sharedParts,
          name: item.name,
          price: item.price,
        });
      }
    }

    this.selectedOption = result;
    this.isClosing = true;
  }

  cancelDivision(item: ItemToShare) {
    this.skipAlreadyDividedCheck = true;
    this.selectedOption = {
      action: 'cancel-division',
      item: item
    };
    this.isClosing = true;
  }

  cancel() {
    this.selectedOption = null;
    this.isClosing = true;
  }

   onAnimationEnd() {
    if (this.isClosing)
      this.dialogRef.close(this.selectedOption);
  }
}
